import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import { ChevronLeft, ChevronRight, Plus, Pencil, CheckCircle2, Ban, X } from "lucide-react";
import { PageHeader, Card, Btn, Segmented, Pill } from "../ui";
import { usePartner, APPOINTMENT_TYPE_LABELS, APPOINTMENT_LOCATION_LABELS } from "../PartnerContext";
import { AppointmentModal } from "../components/AppointmentModal";

dayjs.locale("fr");

const HOUR_START = 7;
const HOUR_END = 21;
const ROW_H = 52;
const HOURS = Array.from({ length: HOUR_END - HOUR_START }, (_, i) => HOUR_START + i);

const timeToY = (iso) => {
  const d = dayjs(iso);
  const h = d.hour() + d.minute() / 60;
  return Math.max(0, (h - HOUR_START) * ROW_H);
};
const durationToH = (startIso, endIso) => Math.max(28, (dayjs(endIso).diff(dayjs(startIso), "minute") / 60) * ROW_H);

const STATUS_TONE = { PLANIFIE: "petrol", REALISE: "sage", ANNULE: "stone" };

const EventBlock = ({ a, onClick }) => (
  <button
    type="button"
    data-testid={`agenda-event-${a.id}`}
    onClick={() => onClick(a)}
    style={{ top: timeToY(a.start_at), height: durationToH(a.start_at, a.end_at) }}
    className={`absolute left-1 right-1 overflow-hidden rounded-lg px-2 py-1 text-left text-[11px] leading-tight shadow-sm transition-opacity ${
      a.status === "ANNULE" ? "bg-stone-200 text-stone-500 opacity-70 line-through" : a.status === "REALISE" ? "bg-sage/20 text-sage" : "bg-petrol text-white"
    }`}
  >
    <span className="block truncate font-bold">{dayjs(a.start_at).format("HH:mm")} · {a.prospect_name}</span>
    <span className="block truncate opacity-80">{APPOINTMENT_TYPE_LABELS[a.appointment_type]}</span>
  </button>
);

const DayColumn = ({ date, appts, onClickEvent }) => (
  <div className="relative border-l border-[#EDF0F1]" style={{ height: (HOUR_END - HOUR_START) * ROW_H }}>
    {HOURS.map((h) => (
      <div key={h} className="absolute inset-x-0 border-t border-[#F0F2F3]" style={{ top: (h - HOUR_START) * ROW_H }} />
    ))}
    {appts.filter((a) => dayjs(a.start_at).isSame(date, "day")).map((a) => <EventBlock key={a.id} a={a} onClick={onClickEvent} />)}
  </div>
);

const AgendaDetailPanel = ({ appointment, onClose, onEdit }) => {
  const { setAppointmentStatus, deleteAppointment } = usePartner();
  if (!appointment) return null;
  return (
    <div data-testid="agenda-detail-panel" className="fixed inset-x-0 bottom-0 z-[500] rounded-t-3xl border border-[#E3E8E9] bg-white p-5 shadow-2xl sm:inset-x-auto sm:bottom-6 sm:right-6 sm:w-96 sm:rounded-2xl">
      <div className="flex items-start justify-between gap-2">
        <div>
          <Pill tone={STATUS_TONE[appointment.status]}>{appointment.status === "PLANIFIE" ? "Prévu" : appointment.status === "REALISE" ? "Réalisé" : "Annulé"}</Pill>
          <p className="mt-2 font-display text-lg font-medium text-navy">{appointment.prospect_name}</p>
        </div>
        <button data-testid="agenda-detail-close" onClick={onClose} className="rounded-full border border-[#D9E0E2] p-1.5 text-stone-500 hover:text-petrol"><X className="h-4 w-4" /></button>
      </div>
      <p className="mt-2 text-sm text-stone-500">{dayjs(appointment.start_at).format("dddd D MMMM YYYY")} · {dayjs(appointment.start_at).format("HH:mm")} – {dayjs(appointment.end_at).format("HH:mm")}</p>
      <p className="mt-1 text-sm text-stone-500">{APPOINTMENT_TYPE_LABELS[appointment.appointment_type]}</p>
      <p className="mt-1 text-sm text-stone-500">{APPOINTMENT_LOCATION_LABELS[appointment.location_type]}{appointment.location_address ? ` · ${appointment.location_address}` : ""}</p>
      {appointment.notes && <p className="mt-2 rounded-lg bg-petrol-bg/50 p-2 text-xs text-stone-600">{appointment.notes}</p>}
      <div className="mt-4 flex flex-wrap gap-2">
        <Btn variant="ghost" testid="agenda-detail-edit" onClick={() => onEdit(appointment)}><Pencil className="h-3.5 w-3.5" /> Modifier</Btn>
        {appointment.status === "PLANIFIE" && (
          <>
            <Btn variant="ghost" testid="agenda-detail-done" onClick={async () => { await setAppointmentStatus(appointment.id, "REALISE"); onClose(); }}><CheckCircle2 className="h-3.5 w-3.5" /> Réalisé</Btn>
            <Btn variant="ghost" testid="agenda-detail-cancel" onClick={async () => { await setAppointmentStatus(appointment.id, "ANNULE"); onClose(); }}><Ban className="h-3.5 w-3.5" /> Annuler</Btn>
          </>
        )}
        <Btn variant="ghost" testid="agenda-detail-delete" onClick={async () => { await deleteAppointment(appointment.id); onClose(); }}><X className="h-3.5 w-3.5" /> Supprimer</Btn>
      </div>
    </div>
  );
};

export default function PartnerAgenda() {
  const { appointments } = usePartner();
  const [searchParams] = useSearchParams();
  const initialDate = searchParams.get("date");
  const [viewMode, setViewMode] = useState("week");
  const [currentDate, setCurrentDate] = useState(() => (initialDate ? dayjs(initialDate) : dayjs()));
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (initialDate) {
      setCurrentDate(dayjs(initialDate));
      setViewMode("day");
    }
  }, [initialDate]);

  const goToday = () => setCurrentDate(dayjs());
  const goPrev = () => setCurrentDate((d) => d.subtract(1, viewMode === "month" ? "month" : viewMode));
  const goNext = () => setCurrentDate((d) => d.add(1, viewMode === "month" ? "month" : viewMode));

  const openDetail = (a) => setDetail(a);
  const openEditFromDetail = (a) => { setDetail(null); setEditing(a); setModalOpen(true); };
  const openCreate = () => { setEditing(null); setModalOpen(true); };

  const weekDays = useMemo(() => {
    const dow = currentDate.day();
    const diffToMonday = dow === 0 ? -6 : 1 - dow;
    const monday = currentDate.add(diffToMonday, "day").startOf("day");
    return Array.from({ length: 7 }, (_, i) => monday.add(i, "day"));
  }, [currentDate]);

  const monthGrid = useMemo(() => {
    const startOfMonth = currentDate.startOf("month");
    const firstWeekday = startOfMonth.day() === 0 ? 6 : startOfMonth.day() - 1;
    const gridStart = startOfMonth.subtract(firstWeekday, "day");
    return Array.from({ length: 42 }, (_, i) => gridStart.add(i, "day"));
  }, [currentDate]);

  const rangeLabel = useMemo(() => {
    if (viewMode === "day") return currentDate.format("dddd D MMMM YYYY");
    if (viewMode === "month") return currentDate.format("MMMM YYYY");
    const start = weekDays[0], end = weekDays[6];
    return `${start.format("D MMM")} – ${end.format("D MMM YYYY")}`;
  }, [viewMode, currentDate, weekDays]);

  return (
    <div data-testid="partner-agenda">
      <PageHeader
        eyebrow="Organisation"
        title="Agenda"
        sub="Vos rendez-vous, reliés directement à vos prospects et clients."
        actions={
          <Btn testid="agenda-create-open" onClick={openCreate}><Plus className="h-4 w-4" /> Nouveau rendez-vous</Btn>
        }
      />

      <Card className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <button data-testid="agenda-nav-prev" onClick={goPrev} className="rounded-full border border-[#D9E0E2] p-2 text-navy hover:border-petrol hover:text-petrol"><ChevronLeft className="h-4 w-4" /></button>
          <button data-testid="agenda-nav-today" onClick={goToday} className="rounded-full border border-[#D9E0E2] px-3 py-2 text-xs font-bold text-navy hover:border-petrol hover:text-petrol">Aujourd'hui</button>
          <button data-testid="agenda-nav-next" onClick={goNext} className="rounded-full border border-[#D9E0E2] p-2 text-navy hover:border-petrol hover:text-petrol"><ChevronRight className="h-4 w-4" /></button>
          <p data-testid="agenda-range-label" className="ml-2 text-sm font-bold capitalize text-navy">{rangeLabel}</p>
        </div>
        <Segmented
          testid="agenda-view-mode"
          size="sm"
          value={viewMode}
          onChange={setViewMode}
          options={[{ value: "day", label: "Jour" }, { value: "week", label: "Semaine" }, { value: "month", label: "Mois" }]}
        />
      </Card>

      {viewMode === "day" && (
        <Card className="mt-4 overflow-x-auto p-4">
          <div className="flex gap-2" style={{ minWidth: 360 }}>
            <div className="w-12 shrink-0">
              <div style={{ height: 24 }} />
              <div className="relative" style={{ height: (HOUR_END - HOUR_START) * ROW_H }}>
                {HOURS.map((h) => (
                  <div key={h} className="absolute -translate-y-2 text-[10px] text-stone-400" style={{ top: (h - HOUR_START) * ROW_H }}>{h}:00</div>
                ))}
              </div>
            </div>
            <div className="flex-1">
              <div className="text-center text-xs font-bold text-navy" style={{ height: 24 }}>{currentDate.format("dddd D MMM")}</div>
              <DayColumn date={currentDate} appts={appointments} onClickEvent={openDetail} />
            </div>
          </div>
        </Card>
      )}

      {viewMode === "week" && (
        <Card className="mt-4 overflow-x-auto p-4">
          <div className="flex gap-1" style={{ minWidth: 900 }}>
            <div className="w-12 shrink-0">
              <div style={{ height: 24 }} />
              <div className="relative" style={{ height: (HOUR_END - HOUR_START) * ROW_H }}>
                {HOURS.map((h) => (
                  <div key={h} className="absolute -translate-y-2 text-[10px] text-stone-400" style={{ top: (h - HOUR_START) * ROW_H }}>{h}:00</div>
                ))}
              </div>
            </div>
            {weekDays.map((d) => (
              <div key={d.format("YYYY-MM-DD")} className="min-w-0 flex-1">
                <div data-testid={`agenda-week-day-${d.format("YYYY-MM-DD")}`} className={`text-center text-xs font-bold capitalize ${d.isSame(dayjs(), "day") ? "text-cognac" : "text-navy"}`} style={{ height: 24 }}>
                  {d.format("ddd D")}
                </div>
                <DayColumn date={d} appts={appointments} onClickEvent={openDetail} />
              </div>
            ))}
          </div>
        </Card>
      )}

      {viewMode === "month" && (
        <Card className="mt-4 p-4">
          <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-bold text-stone-400">
            {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((d) => <div key={d}>{d}</div>)}
          </div>
          <div className="mt-1 grid grid-cols-7 gap-1">
            {monthGrid.map((d) => {
              const dayAppts = appointments.filter((a) => dayjs(a.start_at).isSame(d, "day"));
              const inMonth = d.isSame(currentDate, "month");
              return (
                <button
                  type="button"
                  key={d.format("YYYY-MM-DD")}
                  data-testid={`agenda-month-day-${d.format("YYYY-MM-DD")}`}
                  onClick={() => { setCurrentDate(d); setViewMode("day"); }}
                  className={`min-h-[84px] rounded-lg border p-1.5 text-left text-[11px] transition-colors ${
                    inMonth ? "border-[#EDF0F1] bg-white hover:border-petrol/40" : "border-transparent bg-petrol-bg/20 text-stone-300"
                  } ${d.isSame(dayjs(), "day") ? "ring-2 ring-cognac/50" : ""}`}
                >
                  <span className="font-bold text-navy">{d.format("D")}</span>
                  <div className="mt-1 space-y-0.5">
                    {dayAppts.slice(0, 2).map((a) => (
                      <span key={a.id} className={`block truncate rounded px-1 py-0.5 ${a.status === "ANNULE" ? "bg-stone-100 text-stone-400 line-through" : "bg-petrol-tint text-petrol"}`}>
                        {dayjs(a.start_at).format("HH:mm")} {a.prospect_name}
                      </span>
                    ))}
                    {dayAppts.length > 2 && <span className="block text-[10px] text-stone-400">+{dayAppts.length - 2}</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </Card>
      )}

      <AgendaDetailPanel appointment={detail} onClose={() => setDetail(null)} onEdit={openEditFromDetail} />
      <AppointmentModal open={modalOpen} onClose={() => setModalOpen(false)} editing={editing} />
    </div>
  );
}
